import { AttachmentList } from '@common/data-models/complex-types/attachment-list';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { LookupList } from '@common/data-models/complex-types/lookup-list';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { Record } from '@common/data-models/records/record';
import { ObjectTypes } from '@common/enums/object-types';
import { IPlaybookService } from '@playbook/interfaces/i-playbook-service';
import { Address } from '@suite/common/data-models/complex-types/address';

export class TopicRecord extends Record {
    public static playbookService: IPlaybookService;

    protected getExcludedFields(): { [id: string]: boolean } {
        const excluded: { [id: string]: boolean } = {};
        excluded[PSTFieldNames.Title] = true;
        excluded[PSTFieldNames.Description] = true;
        excluded[PSTFieldNames.PSTDesk] = true;
        excluded[PSTFieldNames.PSTTags] = true;
        excluded[PSTFieldNames.PSTTopicStatus] = true;
        excluded[PSTFieldNames.PSTTopicType] = true;
        excluded[PSTFieldNames.PSTTopicStoryOwner] = true;
        excluded[PSTFieldNames.Owner] = true;
        excluded[PSTFieldNames.PSTExternalFiles] = true;
        excluded[PSTFieldNames.PSTApproved] = true;
        return excluded;
    }

    public initialize() {
        this.type = ObjectTypes.TOPIC;
        super.initialize();

        super.setupRequiredFieldsGroups('Topic');
        // Add a group for assignments
        this.requiredFieldsGroups.Assignments = true;
        this.requiredFieldsGroups.Script = true;
    }

    public get categories(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicCategory, new MultiValue<string>());
    }
    public set categories(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTTopicCategory, value);
    }

    public get department(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTDesk, new MultiValue<string>());
    }
    public set department(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTDesk, value);
    }

    public get notes(): string {
        return this.getAs<string>(PSTFieldNames.PSTTopicNotes);
    }
    public set notes(value: string) {
        this.set(PSTFieldNames.PSTTopicNotes, value);
    }

    public get topicSlug(): string {
        return this.getAs<string>(PSTFieldNames.Title);
    }
    public set topicSlug(value: string) {
        this.set(PSTFieldNames.Title, value);
    }

    public get status(): string {
        return this.getAs<string>(PSTFieldNames.PSTTopicStatus);
    }
    public set status(value: string) {
        this.set(PSTFieldNames.PSTTopicStatus, value);
    }

    public get source(): string {
        return this.getAs<string>(PSTFieldNames.PSTTopicSource);
    }
    public set source(value: string) {
        this.set(PSTFieldNames.PSTTopicSource, value);
    }

    public get storyOwner(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicStoryOwner, new MultiValue<string>());
    }
    public set storyOwner(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTTopicStoryOwner, value);
    }

    public get summary(): string {
        return this.getAs<string>(PSTFieldNames.Description);
    }
    public set summary(value: string) {
        this.set(PSTFieldNames.Description, value);
    }

    public get tags(): LookupList {
        return this.getAs<LookupList>(PSTFieldNames.PSTTags, new LookupList());
    }
    public set tags(value: LookupList) {
        this.set(PSTFieldNames.PSTTags, value);
    }

    public get topicEventStart(): DateTime {
        return this.getAs<DateTime>(PSTFieldNames.PSTTopicEventStartDate);
    }
    public set topicEventStart(value: DateTime) {
        this.set(PSTFieldNames.PSTTopicEventStartDate, value);
    }

    public get topicType(): MultiValue<string> {
        return this.getAs<MultiValue<string>>(PSTFieldNames.PSTTopicType, new MultiValue<string>());
    }
    public set topicType(value: MultiValue<string>) {
        this.set(PSTFieldNames.PSTTopicType, value);
    }

    public get fileDate(): DateTime {
        return this.getAs<DateTime>(PSTFieldNames.TargetPublicationDate);
    }
    public set fileDate(value: DateTime) {
        this.set(PSTFieldNames.TargetPublicationDate, value);
    }

    public get attachments(): AttachmentList {
        return this.getAs<AttachmentList>(PSTFieldNames.PSTExternalFiles, new AttachmentList());
    }
    public set attachments(value: AttachmentList) {
        this.set(PSTFieldNames.PSTExternalFiles, value);
    }

    public get pstApproved(): boolean {
        return this.getAs<boolean>(PSTFieldNames.PSTApproved);
    }
    public set pstApproved(value: boolean) {
        this.set(PSTFieldNames.PSTApproved, value);
    }

    public get location(): Address {
        return this.getAs<Address>(PSTFieldNames.PSTTopicAddress, new Address());
    }
    public set location(value: Address) {
        this.set(PSTFieldNames.PSTTopicAddress, value);
    }

    // get children(): AssignmentRecord[] {
    //     return this._children as AssignmentRecord[];
    // }
    // set children(value: AssignmentRecord[]) {
    //     this._children = value;
    // }

    // public copyDataAndChildrenTo(target: CollectionRecord, preserveGuids?: boolean, isTemplate?: boolean, fieldsToSkip?: { [field: string]: boolean }) {
    //     this.copyDataTo(target, preserveGuids, fieldsToSkip);
    //     if (isTemplate) {
    //         fieldsToSkip[PSTFieldNames.Title.toLowerCase()] = false;
    //     }
    //     for (const child of this.getChildren()) {
    //         const newChild = new AssignmentRecord();
    //         newChild.guid = undefined;
    //         child.copyDataTo(newChild, preserveGuids, fieldsToSkip);
    //         target.addChildren(newChild);
    //     }
    // }
    public get getSecurityPrivsWrite(): boolean {
        const desk = this.department;
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].topic.write) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.write;
                }
            }
            return ret;
        }
        return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.write;
    }

    public getSecurityPrivsWriteChildren(): boolean {
        const desk = this.department;
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].assignment.write) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
                }
            }
            return ret;
        }
        return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.write;
    }

    public getSecurityPrivsRead(): boolean {
        const desk = this.department;
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].topic.read) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.read;
                }
            }
            return ret;
        }
        return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].topic.read;
    }

    public getSecurityPrivsReadChildren(): boolean {
        const desk = this.department;
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].assignment.read) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
                }
            }
            return ret;
        }
        return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].assignment.read;
    }

    get getRequiredProperties(): { [id: string]: FieldDef } {
        return TopicRecord.playbookService.TopicRequiredFieldDefsDict;
    }

    // public checkRequiredProperties() {
    //     super.checkRequiredProperties();
    //     // Check assignments
    //     this.requiredFieldsGroups.Assignments = true;
    //     const children = this.getChildren();
    //     if (children.length > 0) {
    //         for (const child of children) {
    //             child.checkRequiredProperties();
    //             if (!child.isRequiredFieldsPopulated) {
    //                 this.isRequiredFieldsPopulated = false;
    //                 this.requiredFieldsGroups.Assignments = false;
    //                 return;
    //             }
    //         }
    //     }
    // }

    private static _fieldDefsDict: { [id: string]: FieldDef } = null;
    public get fieldDefsDict(): { [id: string]: FieldDef } {
        if (!TopicRecord._fieldDefsDict) {
            TopicRecord._fieldDefsDict = {};
            for (const key in Record.globalTablesService.FieldDefsDict) {
                if (Record.globalTablesService.FieldDefsDict[key].TopicProp) {
                    TopicRecord._fieldDefsDict[key] = Record.globalTablesService.FieldDefsDict[key];
                }
            }
        }
        return TopicRecord._fieldDefsDict;
    }
}
